import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';

export const useAnalytics = () => {
  const posthog = usePostHog();

  const capture = useCallback((event: string, properties?: Record<string, any>) => {
    if (posthog && event) {
      posthog.capture(event, properties);
    }

    // Also send to Google Analytics for backward compatibility
    if (typeof window !== 'undefined' && window.gtag && event) {
      window.gtag('event', event, properties);
    }
  }, [posthog]);

  const identify = useCallback((userId: string, properties?: Record<string, any>) => {
    if (posthog && userId) {
      posthog.identify(userId, properties);
    }
  }, [posthog]);

  // ==================== ERROR TRACKING ====================
  const trackError = useCallback((error: Error, context?: string, additionalProps?: Record<string, any>) => {
    capture('error_occurred', {
      error_message: error.message,
      error_stack: error.stack,
      context,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      timestamp: new Date().toISOString(),
      ...additionalProps
    });
  }, [capture]);

  // ==================== SEARCH TRACKING ====================
  const trackSearch = useCallback((query: string, resultCount: number, duration?: number) => {
    capture('search_performed', {
      query_length: query.length,
      result_count: resultCount,
      has_results: resultCount > 0,
      duration_ms: duration,
      query: query.substring(0, 100) // Truncate for privacy
    });
  }, [capture]);

  // ==================== MESSAGE TRACKING ====================
  const trackMessageSent = useCallback((properties: {
    chatId?: string;
    mode: string;
    hasAttachments?: boolean;
    attachmentTypes?: string[];
    messageLength: number;
    isEdit?: boolean;
    isRegenerate?: boolean;
    modelId?: string;
    useReasoning?: boolean;
  }) => {
    capture('message_sent', properties);
  }, [capture]);

  const trackMessageReceived = useCallback((properties: {
    chatId?: string;
    mode: string;
    responseLength: number;
    duration?: number;
    modelId?: string;
    wasStoppedEarly?: boolean;
  }) => {
    capture('message_received', properties);
  }, [capture]);

  const trackMessageEdit = useCallback((properties: {
    chatId?: string;
    messageId: string;
    mode: string;
  }) => {
    capture('message_edited', properties);
  }, [capture]);

  const trackMessageRegenerate = useCallback((properties: {
    chatId?: string;
    messageId: string;
    mode: string;
  }) => {
    capture('message_regenerated', properties);
  }, [capture]);

  // ==================== CHAT TRACKING ====================
  const trackChatCreated = useCallback((properties?: {
    chatId?: string;
    source?: 'manual' | 'first_message' | 'space';
  }) => {
    capture('chat_created', properties);
  }, [capture]);

  const trackChatDeleted = useCallback((properties?: {
    chatId?: string;
    messageCount?: number;
  }) => {
    capture('chat_deleted', properties);
  }, [capture]);

  const trackChatRenamed = useCallback((properties?: {
    chatId?: string;
  }) => {
    capture('chat_renamed', properties);
  }, [capture]);

  // ==================== MODEL & MODE TRACKING ====================
  const trackModelChange = useCallback((properties: {
    previousModel?: string;
    newModel: string;
  }) => {
    capture('model_changed', properties);
  }, [capture]);

  const trackModeChange = useCallback((properties: {
    previousMode?: string;
    newMode: string;
    isCustomMode?: boolean;
  }) => {
    capture('mode_changed', properties);
  }, [capture]);

  // ==================== ATTACHMENT TRACKING ====================
  const trackFileAttached = useCallback((properties: {
    fileType: string;
    fileSize?: number;
    source?: 'upload' | 'drag_drop';
  }) => {
    capture('file_attached', properties);
  }, [capture]);

  const trackImageAttached = useCallback((properties: {
    imageType: string;
    source?: 'upload' | 'paste' | 'drag_drop';
  }) => {
    capture('image_attached', properties);
  }, [capture]);

  // ==================== STUDY FEATURES TRACKING ====================
  const trackResourceOpened = useCallback((properties: {
    resourceType: 'note' | 'code' | 'quiz' | 'pdf';
    resourceId?: string;
    chatId?: string;
  }) => {
    capture('resource_opened', properties);
  }, [capture]);

  const trackResourceCreated = useCallback((properties: {
    resourceType: 'note' | 'code' | 'quiz' | 'pdf';
    source?: 'manual' | 'ai_generated';
  }) => {
    capture('resource_created', properties);
  }, [capture]);

  const trackQuizAttempted = useCallback((properties: {
    quizId?: string;
    questionCount?: number;
  }) => {
    capture('quiz_attempted', properties);
  }, [capture]);

  const trackStudyPanelToggled = useCallback((properties: {
    action: 'opened' | 'closed';
  }) => {
    capture('study_panel_toggled', properties);
  }, [capture]);

  // ==================== SPACE TRACKING ====================
  const trackSpaceCreated = useCallback((properties?: {
    spaceId?: string;
    hasAttachments?: boolean;
  }) => {
    capture('space_created', properties);
  }, [capture]);

  const trackSpaceDeleted = useCallback((properties?: {
    spaceId?: string;
    chatCount?: number;
  }) => {
    capture('space_deleted', properties);
  }, [capture]);

  const trackSpaceChatCreated = useCallback((properties?: {
    spaceId?: string;
    chatId?: string;
  }) => {
    capture('space_chat_created', properties);
  }, [capture]);

  // ==================== PRICING & SUBSCRIPTION TRACKING ====================
  const trackPricingViewed = useCallback((properties?: {
    source?: string;
  }) => {
    capture('pricing_viewed', properties);
  }, [capture]);

  const trackCheckoutStarted = useCallback((properties: {
    plan: string;
    interval: 'monthly' | 'yearly';
    price?: number;
    currency?: string;
    source?: string;
  }) => {
    capture('checkout_started', properties);
  }, [capture]);

  const trackSubscriptionChanged = useCallback((properties: {
    action: 'upgraded' | 'downgraded' | 'cancelled' | 'reactivated';
    fromPlan?: string;
    toPlan: string;
  }) => {
    capture('subscription_changed', properties);
  }, [capture]);

  // ==================== VOICE/TTS TRACKING ====================
  const trackVoiceUsed = useCallback((properties: {
    feature: 'tts' | 'voice_input';
    voiceId?: string;
    duration?: number;
  }) => {
    capture('voice_feature_used', properties);
  }, [capture]);

  // ==================== SETTINGS TRACKING ====================
  const trackSettingsChanged = useCallback((properties: {
    setting: string;
    oldValue?: any;
    newValue: any;
  }) => {
    capture('settings_changed', properties);
  }, [capture]);

  // ==================== ONBOARDING & ENGAGEMENT ====================
  const trackOnboardingStep = useCallback((properties: {
    step: string;
    completed: boolean;
  }) => {
    capture('onboarding_step', properties);
  }, [capture]);

  const trackFeatureDiscovered = useCallback((properties: {
    feature: string;
    discoveryMethod?: string;
  }) => {
    capture('feature_discovered', properties);
  }, [capture]);

  const trackUserEngagement = useCallback((action: string, context?: Record<string, any>) => {
    capture('user_engagement', {
      action,
      ...context
    });
  }, [capture]);

  // ==================== PAGE VIEW TRACKING ====================
  const trackPageView = useCallback((properties?: {
    page: string;
    referrer?: string;
  }) => {
    capture('page_viewed', properties);
  }, [capture]);

  return {
    // Core
    capture,
    identify,
    
    // Errors
    trackError,
    
    // Search
    trackSearch,
    
    // Messages
    trackMessageSent,
    trackMessageReceived,
    trackMessageEdit,
    trackMessageRegenerate,
    
    // Chats
    trackChatCreated,
    trackChatDeleted,
    trackChatRenamed,
    
    // Models & Modes
    trackModelChange,
    trackModeChange,
    
    // Attachments
    trackFileAttached,
    trackImageAttached,
    
    // Study Features
    trackResourceOpened,
    trackResourceCreated,
    trackQuizAttempted,
    trackStudyPanelToggled,
    
    // Spaces
    trackSpaceCreated,
    trackSpaceDeleted,
    trackSpaceChatCreated,
    
    // Pricing
    trackPricingViewed,
    trackCheckoutStarted,
    trackSubscriptionChanged,
    
    // Voice/TTS
    trackVoiceUsed,
    
    // Settings
    trackSettingsChanged,
    
    // Onboarding
    trackOnboardingStep,
    trackFeatureDiscovered,
    trackUserEngagement,
    
    // Page Views
    trackPageView
  };
};

// Legacy gtag-only hook for gradual migration
export const useLegacyAnalytics = () => {
  const trackEvent = useCallback((event: string, parameters?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event, parameters);
    }
  }, []);

  return { trackEvent };
};
